import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/utils/misc";
import { fetchSearchLight } from "@/utils/search-light";
import { ChevronDown, Link2, Loader, LocateFixed, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import wretch from "wretch";
import { FieldPicker } from "./field-picker";
import { type FormulaContext, useFormulaScopes } from "./formula/api";
import { FilterSchemaTile } from "./schema-tile";
import { originLabel } from "./tree-utils";
import type { FilterSchema, GeoOrigin, GeoValue, SchemaField } from "./types";
import { loadFilterSchema, resolvePath, useResolvedPath } from "./use-filter-schema";

const controlClass = "h-8 rounded-sm bg-white text-sm shadow-sm";

type Place = { label: string; lat: number; lng: number };

/** Addresses and towns of the Base Adresse Nationale (the CRM geocodes its records with it too). */
async function searchPlaces(query: string, signal: AbortSignal): Promise<Place[]> {
    const data = (await wretch("https://api-adresse.data.gouv.fr")
        .options({ signal })
        .url(`/search/?${new URLSearchParams({ q: query, limit: "6" })}`)
        .get()
        .json()) as { features?: { properties: { label: string }; geometry?: { coordinates?: number[] } }[] };
    return (data.features ?? []).flatMap(feature => {
        const [lng, lat] = feature.geometry?.coordinates ?? [];
        return typeof lat === "number" && typeof lng === "number" ? [{ label: feature.properties.label, lat, lng }] : [];
    });
}

/** « 45.76, 4.83 » typed as is. */
function parseCoordinates(text: string): { lat: number; lng: number } | null {
    const match = text.trim().match(/^(-?\d+(?:[.,]\d+)?)\s*[;,\s]\s*(-?\d+(?:[.,]\d+)?)$/);
    if (!match) return null;
    const lat = Number(match[1].replace(",", "."));
    const lng = Number(match[2].replace(",", "."));
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

type Source = "place" | "record" | "field";

const SOURCES: Record<Source, { label: string; icon: typeof MapPin }> = {
    place: { label: "Place", icon: MapPin },
    record: { label: "Record", icon: Link2 },
    field: { label: "Field", icon: LocateFixed }
};

/**
 * A record around a condition, by its role rather than its object (two of them are often
 * opportunities): the one the condition tests, the one the list shows, or one in a block above.
 */
function scopeRole(index: number, count: number, name: string): string {
    const object = name.toLowerCase();
    if (index === count - 1) return `This ${object}`;
    return index === 0 ? `The listed ${object}` : `The ${object} above`;
}

/** The kind of origin the input group shows a value under: a fixed point is a place. */
const sourceOf = (origin?: GeoOrigin | null): Source => (!origin || origin.type === "point" ? "place" : origin.type);

const isLocation = (field: SchemaField) => field.type === "geo";

/**
 * Whose records hold the location `path` names from `rootModel`: the deepest CRM object on the
 * path (`account__location` → accounts, measured from their location), or the last one before an
 * object without records of its own (`apprenticeship_ext__offer_location` → the root's records).
 */
async function originTarget(rootModel: string, path: string): Promise<{ schema: FilterSchema; point: string } | null> {
    const segments = path.split("__");
    let schema = await loadFilterSchema(rootModel);
    const schemas = [schema];
    for (const name of segments.slice(0, -1)) {
        const hop = schema.relations.find(relation => relation.name === name && relation.kind === "to_one");
        if (!hop) return null;
        schema = await loadFilterSchema(hop.model);
        schemas.push(schema);
    }
    for (let index = schemas.length - 1; index >= 0; index--) {
        if (schemas[index].object_url) return { schema: schemas[index], point: segments.slice(index).join("__") };
    }
    return null;
}

/**
 * Where a distance is measured from: an address or a town, coordinates, or a record. A record's
 * location is picked like a condition's field (Account › Location, from `rootModel`), then the
 * record among those of the object holding it. It is read when the list loads: the record moves,
 * the list follows.
 */
export function GeoOriginPicker({
    value,
    onChange,
    rootModel,
    defaultPoint,
    scope,
    invalid,
    className
}: {
    value?: GeoOrigin | null;
    onChange: (origin: GeoOrigin) => void;
    /** The object the location of a record origin is picked from (the filtered or sorted one). */
    rootModel?: string | null;
    /** The location picked first: the one the distance is measured to. */
    defaultPoint?: string | null;
    /** The records around a condition (none in a sort): a distance can be measured from their own locations. */
    scope?: FormulaContext | null;
    invalid?: boolean;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    const [source, setSource] = useState<Source>(sourceOf(value));
    const scopes = useFormulaScopes(scope ?? { rootModel: "", relations: [] }, Boolean(scope) && open && source === "field");
    const [pointPath, setPointPath] = useState(defaultPoint ?? "");
    const resolvedPoint = useResolvedPath(rootModel ?? "", rootModel ? pointPath : "");
    const [target, setTarget] = useState<{ schema: FilterSchema; point: string } | null>(null);
    const [query, setQuery] = useState("");
    const debounced = useDebounce(query, 300);
    const [places, setPlaces] = useState<Place[]>([]);
    const [records, setRecords] = useState<{ id: string; label: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const sources: Source[] = rootModel ? (scope ? ["place", "record", "field"] : ["place", "record"]) : ["place"];
    const active: Source = sources.includes(source) ? source : "place";
    const coordinates = active === "place" ? parseCoordinates(query) : null;
    const recordModel = target?.schema.model;

    // The location measured to changed (another field, another sort): start from it again.
    useEffect(() => setPointPath(defaultPoint ?? ""), [defaultPoint]);

    useEffect(() => {
        if (!open || !rootModel || !pointPath) {
            setTarget(null);
            return;
        }
        let cancelled = false;
        originTarget(rootModel, pointPath)
            .then(next => !cancelled && setTarget(next))
            .catch(() => !cancelled && setTarget(null));
        return () => {
            cancelled = true;
        };
    }, [open, rootModel, pointPath]);

    useEffect(() => {
        const text = debounced.trim();
        if (!open || (active === "record" && !recordModel) || text.length < (active === "place" ? 3 : 2) || (active === "place" && parseCoordinates(text))) {
            setPlaces([]);
            setRecords([]);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        // A search the next keystroke aborted settles too: only the current one may touch the state.
        const current = () => !controller.signal.aborted;
        const request =
            active === "place"
                ? searchPlaces(text, controller.signal).then(next => current() && setPlaces(next))
                : fetchSearchLight(text, recordModel as string, controller).then(
                      (data: Record<string, { results?: Array<{ id: number | string; title?: string }> }> | null) =>
                          current() && setRecords((data?.[recordModel as string]?.results ?? []).map(row => ({ id: String(row.id), label: row.title || `#${row.id}` })))
                  );
        request
            .catch(() => {
                if (!current()) return;
                setPlaces([]);
                setRecords([]);
            })
            .finally(() => current() && setLoading(false));
        return () => controller.abort();
    }, [debounced, active, open, recordModel]);

    const pick = (origin: GeoOrigin) => {
        onChange(origin);
        setOpen(false);
        setQuery("");
    };
    const pointLabels = resolvedPoint?.labels ?? [];

    // The value shows under the kind it is; a kind just switched to waits for its pick.
    const shown = value && sourceOf(value) === active ? value : null;
    const SourceIcon = SOURCES[active].icon;
    const pickField = async (index: number, model: string, role: string, path: string) => {
        const resolved = await resolvePath(model, path);
        pick({ type: "field", scope: index, point: path, label: [role, ...(resolved?.labels ?? [path])].join(" › ") });
    };

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverAnchor asChild>
                <div className={cn("flex min-w-[240px] max-w-[360px]", className)}>
                    {rootModel && (
                        <Select
                            value={active}
                            onValueChange={next => {
                                setSource(next as Source);
                                setQuery("");
                                setOpen(true);
                            }}
                        >
                            <SelectTrigger
                                className={cn(controlClass, "w-auto gap-1.5 rounded-r-none border-r-0 bg-gray-50 px-2 text-gray-700", invalid && !value && "border-red-500")}
                                aria-label="Measure from"
                            >
                                <SourceIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                                <span>{SOURCES[active].label}</span>
                            </SelectTrigger>
                            <SelectContent>
                                {sources.map(item => {
                                    const Icon = SOURCES[item].icon;
                                    return (
                                        <SelectItem key={item} value={item}>
                                            <span className="flex items-center gap-2">
                                                <Icon className="h-4 w-4 text-gray-500" /> {SOURCES[item].label}
                                            </span>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    )}
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            className={cn(controlClass, "min-w-0 flex-1 justify-between px-2 font-normal", rootModel && "rounded-l-none", invalid && !value && "border-red-500")}
                        >
                            <span className={cn("truncate", !shown && "text-gray-500")}>
                                {shown ? originLabel(shown) : { place: "Search a place", record: "Pick a record", field: "Pick a location" }[active]}
                            </span>
                            <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 text-gray-500" />
                        </Button>
                    </PopoverTrigger>
                </div>
            </PopoverAnchor>
            <PopoverContent className="flex max-h-[var(--radix-popover-content-available-height)] w-[380px] flex-col p-0" align="start" collisionPadding={12}>
                {active === "record" && rootModel && (
                    <div className="flex flex-col gap-1 border-b px-2 py-2">
                        <span className="text-xs text-gray-600">Location of the record</span>
                        <FieldPicker rootModel={rootModel} labels={pointLabels} allowRelated={false} filterField={isLocation} className="w-full" onPick={next => setPointPath(next.path)} />
                    </div>
                )}
                {active === "field" && (
                    <div className="flex flex-col gap-3 px-2 py-2">
                        {!scopes && (
                            <div className="flex items-center gap-2 p-1 text-sm text-gray-500">
                                <Loader className="h-4 w-4 animate-spin" /> Loading...
                            </div>
                        )}
                        {scopes?.map(({ scope: item, schema: itemSchema }, index) => (
                            <div key={item.index} className="flex flex-col gap-1">
                                <span className="text-xs text-gray-600">{scopeRole(index, scopes.length, itemSchema.name)}</span>
                                <FieldPicker
                                    rootModel={item.model}
                                    labels={value?.type === "field" && value.scope === item.index ? [originLabel(value)] : []}
                                    allowRelated={false}
                                    filterField={isLocation}
                                    className="w-full"
                                    onPick={next => pickField(item.index, item.model, scopeRole(index, scopes.length, itemSchema.name), next.path)}
                                />
                            </div>
                        ))}
                        <p className="text-xs text-gray-500">Measured from each record&apos;s own location: every record gets its own distance.</p>
                    </div>
                )}
                {active !== "field" && (
                    <Command shouldFilter={false} className="min-h-0 flex-1">
                        <CommandInput
                            placeholder={active === "place" ? "Address, town, or 45.76, 4.83" : target ? `Search ${target.schema.name_plural.toLowerCase()}...` : "Pick a location first"}
                            disabled={active === "record" && !target}
                            value={query}
                            onValueChange={setQuery}
                        />
                        <CommandList className="max-h-[300px] min-h-0 flex-1">
                            {coordinates && (
                                <CommandGroup heading="Coordinates">
                                    <CommandItem value="coordinates" onSelect={() => pick({ type: "point", ...coordinates })}>
                                        <MapPin className="mr-2 h-4 w-4 text-gray-500" />
                                        {coordinates.lat}, {coordinates.lng}
                                    </CommandItem>
                                </CommandGroup>
                            )}
                            {loading && (
                                <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                                    <Loader className="h-4 w-4 animate-spin" /> Loading...
                                </div>
                            )}
                            {!loading && !coordinates && debounced.trim().length >= 2 && (active === "place" ? places : records).length === 0 && (
                                <div className="p-3 text-sm text-gray-500">No result found</div>
                            )}
                            {active === "place" && places.length > 0 && (
                                <CommandGroup heading="Places">
                                    {places.map(place => (
                                        <CommandItem
                                            key={`${place.label}-${place.lat}-${place.lng}`}
                                            value={`${place.label}-${place.lat}-${place.lng}`}
                                            onSelect={() => pick({ type: "point", ...place })}
                                        >
                                            <MapPin className="mr-2 h-4 w-4 flex-shrink-0 text-gray-500" />
                                            <span className="truncate">{place.label}</span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                            {active === "record" && target && records.length > 0 && (
                                <CommandGroup heading={target.schema.name_plural}>
                                    {records.map(record => (
                                        <CommandItem
                                            key={record.id}
                                            value={record.id}
                                            onSelect={() => pick({ type: "record", model: target.schema.model, id: record.id, point: target.point, label: record.label })}
                                        >
                                            <FilterSchemaTile schema={target.schema} size="sm" />
                                            <span className="ml-2 truncate">{record.label}</span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                )}
                {active === "record" && target && (
                    <p className="shrink-0 border-t px-3 py-2 text-xs text-gray-500">
                        Measured from the {pointLabels.slice(-1)[0]?.toLowerCase() ?? "location"} of the {target.schema.name.toLowerCase()} you pick, read each time the list loads.
                    </p>
                )}
            </PopoverContent>
        </Popover>
    );
}

/** « within [50] km of [Lyon] »: the value of a distance condition. */
export function GeoValueInput({
    value,
    onChange,
    rootModel,
    fieldPath,
    scope,
    invalid
}: {
    value: GeoValue;
    onChange: (value: GeoValue) => void;
    /** The object the condition filters, and the path of its location field. */
    rootModel?: string | null;
    fieldPath?: string | null;
    /** The records around the condition. */
    scope?: FormulaContext | null;
    invalid?: boolean;
}) {
    const radius = value.radius_km == null ? "" : String(value.radius_km);
    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
                <Input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    containerClassName="w-24"
                    className={cn(controlClass, "w-24 tabular-nums", invalid && !(Number(radius) > 0) && "border-red-500")}
                    value={radius}
                    placeholder="50"
                    aria-label="Radius in kilometres"
                    onChange={event => onChange({ ...value, radius_km: event.target.value === "" ? null : Number(event.target.value) })}
                />
                <span className="text-sm text-gray-600">km of</span>
            </div>
            <GeoOriginPicker value={value.origin} onChange={origin => onChange({ ...value, origin })} rootModel={rootModel} defaultPoint={fieldPath} scope={scope} invalid={invalid} />
        </div>
    );
}
